import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Politique de Confidentialité</h1>
          <p className="text-muted-foreground mt-2">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
        </div>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">1. Responsable du traitement</h2>
            <p className="mb-4">
              Emma Burt, éditeur de l'application "Clan", est responsable du traitement de vos données personnelles.
            </p>
            <p>
              Contact : contact@get-clan.com
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">2. Données collectées</h2>
            <p className="mb-4">Nous collectons les données suivantes :</p>
            
            <h3 className="text-lg font-medium mb-3">2.1 Données d'inscription et de profil</h3>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>Adresse email</li>
              <li>Mot de passe (chiffré)</li>
              <li>Nom et prénom (optionnel)</li>
              <li>Photo de profil (optionnel)</li>
              <li>Date de création du compte</li>
            </ul>

            <h3 className="text-lg font-medium mb-3">2.2 Données d'utilisation</h3>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>Événements créés et leurs détails</li>
              <li>Tâches et listes de tâches</li>
              <li>Documents partagés</li>
              <li>Profils des membres de famille créés</li>
              <li>Invitations envoyées</li>
              <li>Données de synchronisation Google Calendar (avec autorisation)</li>
            </ul>

            <h3 className="text-lg font-medium mb-3">2.3 Données techniques</h3>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>Adresse IP</li>
              <li>Type de navigateur et version</li>
              <li>Système d'exploitation</li>
              <li>Données de connexion et d'usage</li>
              <li>Cookies techniques</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">3. Finalités du traitement</h2>
            <p className="mb-4">Vos données sont traitées pour :</p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>Créer et gérer votre compte utilisateur</li>
              <li>Fournir les fonctionnalités de l'application</li>
              <li>Permettre la collaboration familiale</li>
              <li>Synchroniser avec des services tiers (Google Calendar)</li>
              <li>Gérer les abonnements et paiements</li>
              <li>Assurer la sécurité de l'application</li>
              <li>Améliorer nos services</li>
              <li>Respecter nos obligations légales</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">4. Base légale</h2>
            <p className="mb-4">Le traitement de vos données se base sur :</p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li><strong>Exécution du contrat</strong> : pour fournir les services de l'application</li>
              <li><strong>Intérêt légitime</strong> : pour améliorer nos services et assurer la sécurité</li>
              <li><strong>Consentement</strong> : pour les fonctionnalités optionnelles (synchronisation Google Calendar)</li>
              <li><strong>Obligation légale</strong> : pour respecter nos obligations comptables et fiscales</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">5. Partage des données</h2>
            <p className="mb-4">Vos données peuvent être partagées avec :</p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li><strong>Membres de votre famille</strong> : selon les paramètres de partage que vous avez définis</li>
              <li><strong>Prestataires techniques</strong> : Supabase (hébergement), Stripe (paiements)</li>
              <li><strong>Services tiers</strong> : Google (avec votre autorisation pour Calendar)</li>
            </ul>
            <p>
              Nous ne vendons jamais vos données personnelles à des tiers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">6. Transferts internationaux</h2>
            <p>
              Nos données sont hébergées par Supabase, qui utilise des serveurs situés dans l'Union Européenne. 
              Certains sous-traitants peuvent être situés hors UE avec des garanties appropriées (clauses contractuelles types).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">7. Durée de conservation</h2>
            <p className="mb-4">Nous conservons vos données :</p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li><strong>Compte actif</strong> : tant que votre compte existe</li>
              <li><strong>Après suppression</strong> : 30 jours pour permettre une récupération</li>
              <li><strong>Données de facturation</strong> : 10 ans (obligation légale)</li>
              <li><strong>Logs techniques</strong> : 12 mois maximum</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">8. Vos droits</h2>
            <p className="mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li><strong>Droit d'accès</strong> : obtenir une copie de vos données</li>
              <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
              <li><strong>Droit à l'effacement</strong> : supprimer vos données</li>
              <li><strong>Droit à la limitation</strong> : limiter le traitement</li>
              <li><strong>Droit à la portabilité</strong> : récupérer vos données dans un format standard</li>
              <li><strong>Droit d'opposition</strong> : vous opposer au traitement</li>
              <li><strong>Droit de retrait du consentement</strong> : pour les traitements basés sur le consentement</li>
            </ul>
            <p>
              Pour exercer ces droits, contactez-nous à : contact@get-clan.com
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">9. Sécurité</h2>
            <p className="mb-4">Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données :</p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>Chiffrement des données sensibles</li>
              <li>Connexions sécurisées (HTTPS/SSL)</li>
              <li>Authentification sécurisée</li>
              <li>Sauvegardes automatiques</li>
              <li>Accès limité aux données par notre équipe</li>
              <li>Surveillance des accès et des activités suspectes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">10. Cookies</h2>
            <p className="mb-4">Nous utilisons des cookies techniques nécessaires au fonctionnement de l'application :</p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li><strong>Cookies de session</strong> : pour maintenir votre connexion</li>
              <li><strong>Cookies de préférences</strong> : pour sauvegarder vos paramètres</li>
              <li><strong>Cookies de sécurité</strong> : pour prévenir les attaques</li>
            </ul>
            <p>
              Vous pouvez désactivier les cookies dans votre navigateur, mais cela peut affecter le fonctionnement de l'application.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">11. Modifications</h2>
            <p>
              Cette politique peut être modifiée pour refléter les évolutions de l'application ou de la réglementation. 
              Vous serez informé des modifications importantes par email ou via l'application.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">12. Réclamations</h2>
            <p>
              Si vous estimez que le traitement de vos données ne respecte pas la réglementation, 
              vous pouvez déposer une réclamation auprès de la CNIL : www.cnil.fr
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">13. Intégration avec les services Google</h2>
            
            <h3 className="text-lg font-medium mb-3">13.1 Données Google collectées</h3>
            <p className="mb-4">
              Lorsque vous choisissez de vous connecter avec Google, notre application accède uniquement aux données suivantes :
            </p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li><strong>Adresse email</strong> : utilisée pour l'authentification et l'identification de votre compte</li>
              <li><strong>Nom et prénom</strong> : utilisés pour personnaliser votre profil utilisateur dans l'application</li>
              <li><strong>Accès Google Calendar</strong> : utilisé uniquement pour synchroniser vos événements entre l'application et votre calendrier Google</li>
            </ul>

            <h3 className="text-lg font-medium mb-3">13.2 Utilisation des données Google</h3>
            <p className="mb-4">
              Nous utilisons vos données Google exclusivement pour :
            </p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>Créer et authentifier votre compte utilisateur</li>
              <li>Personnaliser votre expérience dans l'application avec votre nom</li>
              <li>Synchroniser vos événements calendrier bidirectionnellement entre Clan et Google Calendar</li>
              <li>Améliorer l'expérience utilisateur en évitant la double saisie d'événements</li>
            </ul>

            <h3 className="text-lg font-medium mb-3">13.3 Partage des données Google</h3>
            <p className="mb-4">
              <strong>Nous ne partageons jamais vos données Google avec des tiers.</strong> Vos données Google restent strictement entre vous et notre application. Nous ne vendons, ne louons, ni ne communiquons vos données Google à des partenaires commerciaux, des annonceurs ou d'autres services tiers.
            </p>

            <h3 className="text-lg font-medium mb-3">13.4 Stockage et protection des données Google</h3>
            <p className="mb-4">
              Concernant la sécurité de vos données Google :
            </p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>Les tokens d'accès Google sont stockés de manière sécurisée et chiffrée</li>
              <li>Nous ne stockons pas de façon permanente le contenu de vos calendriers Google sur nos serveurs</li>
              <li>L'accès aux données Google se fait uniquement au moment de la synchronisation</li>
              <li>Toutes les communications avec les serveurs Google utilisent des protocoles sécurisés (HTTPS)</li>
            </ul>

            <h3 className="text-lg font-medium mb-3">13.5 Contrôle et révocation d'accès</h3>
            <p className="mb-4">
              Vous gardez le contrôle total de vos données Google :
            </p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>L'accès se fait uniquement avec votre autorisation explicite via OAuth 2.0</li>
              <li>Vous pouvez révoquer l'accès à tout moment via les paramètres de votre compte Google</li>
              <li>Vous pouvez également déconnecter Google depuis les paramètres de votre compte Clan</li>
              <li>La révocation de l'accès arrête immédiatement toute synchronisation future</li>
            </ul>

            <h3 className="text-lg font-medium mb-3">13.6 Suppression des données Google</h3>
            <p className="mb-4">
              Concernant la suppression de vos données Google :
            </p>
            <ul className="list-disc list-inside mb-4 ml-4">
              <li>Lors de la déconnexion Google, tous les tokens d'accès sont immédiatement supprimés</li>
              <li>Lors de la suppression de votre compte Clan, toutes les données associées à votre compte Google sont supprimées</li>
              <li>Vous pouvez demander la suppression de vos données Google en nous contactant à contact@get-clan.com</li>
              <li>Les données Google ne sont conservées que tant que la connexion est active</li>
            </ul>

            <p className="mb-4">
              <strong>Important :</strong> Cette politique respecte la politique d'utilisation des données utilisateurs de Google API Services et les conditions d'utilisation des API Google.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">14. Contact</h2>
            <p>
              Pour toute question concernant cette politique de confidentialité ou l'exercice de vos droits :
              <br />
              Email : contact@get-clan.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;